import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  SingleValuePanel,
  TagsTile,
  panelSpanFromLayout,
  type BackofficePageMode,
  type DetailPanelGridItem,
} from '@/components/shared';
import { fetchBackofficeOrganizationSalesSummary } from '../api';
import { formatSalesSummary } from '../format';
import type {
  BackofficeOrganizationDetail,
  BackofficeOrganizationSalesSummaryEntry,
  OrganizationBalanceTab,
} from '../types';
import { ProjectInterviewsListSection, ProjectsListSection } from '../../projects/components';
import type { BackofficeProjectInterviewsResponse } from '../../projects/types';
import {
  OrganizationDetailPanel,
  OrganizationDetailPanelGrid,
} from './OrganizationDetailPanelGrid';
import { OrganizationMembersSection } from './OrganizationMembersSection';
import { OrganizationSalesListSection } from './OrganizationSalesListSection';
import { OrganizationTransactionsListSection } from './OrganizationTransactionsListSection';

const BALANCE_TABS = new Set<OrganizationBalanceTab>(['interview-balance', 'sales']);

function readBalanceTab(value: string | null): OrganizationBalanceTab {
  return value && BALANCE_TABS.has(value as OrganizationBalanceTab)
    ? (value as OrganizationBalanceTab)
    : 'interview-balance';
}

export interface OrganizationBalanceSectionProps {
  organization: BackofficeOrganizationDetail;
  onBalanceAdjusted: () => Promise<void>;
  layoutMode: BackofficePageMode;
  additionalTiles?: DetailPanelGridItem[];
  loadOrganizationInterviews: (
    searchParams: URLSearchParams,
    signal: AbortSignal
  ) => Promise<Pick<BackofficeProjectInterviewsResponse, 'interviews' | 'page'>>;
}

export function OrganizationBalanceSection({
  organization,
  onBalanceAdjusted,
  layoutMode,
  additionalTiles = [],
  loadOrganizationInterviews,
}: OrganizationBalanceSectionProps) {
  const [searchParams] = useSearchParams();
  const [salesSummary, setSalesSummary] = useState<BackofficeOrganizationSalesSummaryEntry[]>([]);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const activeTab = readBalanceTab(searchParams.get('balanceTab'));

  useEffect(() => {
    const controller = new AbortController();
    setSummaryLoading(true);
    setSummaryError(null);

    fetchBackofficeOrganizationSalesSummary(organization.id, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) {
          setSalesSummary(response.items);
        }
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setSalesSummary([]);
        setSummaryError(caught instanceof Error ? caught.message : 'Failed to load sales summary');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setSummaryLoading(false);
        }
      });

    return () => controller.abort();
  }, [organization.id]);

  function getSubtabHref(tab: OrganizationBalanceTab) {
    const next = new URLSearchParams(searchParams);
    if (tab === 'interview-balance') {
      next.delete('balanceTab');
    } else {
      next.set('balanceTab', tab);
    }
    const query = next.toString();
    return `/organizations/${organization.id}?${query}`;
  }

  function getSubtabClassName(tab: OrganizationBalanceTab) {
    return [
      'px-1 pb-3 text-body font-semibold',
      activeTab === tab
        ? 'border-b-2 border-neutral-900 text-neutral-900'
        : 'border-b-2 border-transparent text-neutral-500 hover:text-backoffice-600',
    ].join(' ');
  }

  const salesSummaryValue = summaryLoading
    ? 'Loading'
    : summaryError
      ? 'Unavailable'
      : formatSalesSummary(salesSummary);

  return (
    <section className="flex flex-col gap-5">
      <div className="flex gap-5 border-b border-neutral-200">
        <Link
          to={getSubtabHref('interview-balance')}
          className={getSubtabClassName('interview-balance')}
          aria-current={activeTab === 'interview-balance' ? 'page' : undefined}
        >
          Interview balance
        </Link>
        <Link
          to={getSubtabHref('sales')}
          className={getSubtabClassName('sales')}
          aria-current={activeTab === 'sales' ? 'page' : undefined}
        >
          Sales
        </Link>
      </div>

      <OrganizationDetailPanelGrid
        resetKey={`${organization.id}:${activeTab}`}
        preferenceScope={`details:organization:balance:${activeTab}`}
        layoutMode={layoutMode}
        defaultVisibleItemIds={[
          'interview-balance',
          'sales-summary',
          activeTab === 'sales' ? 'sales-history' : 'transactions',
        ]}
        tileCatalogLabel="Add balance tile"
        items={[
          ...additionalTiles,
          {
            id: 'interview-balance',
            title: 'Interview balance',
            description: 'Current available interview credits for this organization.',
            layout: { x: 0, y: 0, w: 1, h: 1, isResizable: false },
            render: (layout) => (
              <SingleValuePanel
                panelId="interview-balance"
                currentSpan={panelSpanFromLayout(layout, '1x1')}
                title="Interview balance"
                value={
                  organization.interviewBalance === null
                    ? 'Not available'
                    : String(organization.interviewBalance)
                }
                valueTone={organization.interviewBalance === null ? 'neutral' : 'primary'}
              />
            ),
          },
          {
            id: 'sales-summary',
            title: 'Sales summary',
            description: 'Completed sales total grouped by currency.',
            layout: { x: 1, y: 0, w: 2, h: 1, minW: 1, maxW: 4, isResizable: true },
            render: (layout) => (
              <SingleValuePanel
                panelId="sales-summary"
                currentSpan={panelSpanFromLayout(layout, '2x1')}
                title="Sales summary"
                value={salesSummaryValue}
                valueTone={
                  summaryLoading || summaryError || salesSummary.length === 0
                    ? 'neutral'
                    : 'primary'
                }
              />
            ),
          },
          {
            id: 'transactions',
            title: 'Balance transactions',
            description: 'Credit purchases, voucher grants, interview usage, and adjustments.',
            layout: {
              x: 0,
              y: 1,
              w: 4,
              h: 4,
              minW: 2,
              minH: 2,
              maxW: 6,
              maxH: 5,
              isResizable: true,
            },
            render: (layout) => (
              <OrganizationDetailPanel
                title="Balance transactions"
                span="4x4"
                panelId="transactions"
                currentSpan={panelSpanFromLayout(layout, '4x4')}
                contentClassName="-mx-5 -mb-5"
                contentOverflow="hidden"
              >
                <OrganizationTransactionsListSection
                  organizationId={organization.id}
                  organizationName={organization.name}
                  onBalanceAdjusted={onBalanceAdjusted}
                  surface="tile"
                />
              </OrganizationDetailPanel>
            ),
          },
          {
            id: 'sales-history',
            title: 'Sales history',
            description: 'Invoices, payment status, refund state, and invoice links.',
            layout: {
              x: 0,
              y: 5,
              w: 4,
              h: 4,
              minW: 2,
              minH: 2,
              maxW: 6,
              maxH: 5,
              isResizable: true,
            },
            render: (layout) => (
              <OrganizationDetailPanel
                title="Sales history"
                span="4x4"
                panelId="sales-history"
                currentSpan={panelSpanFromLayout(layout, '4x4')}
                contentClassName="-mx-5 -mb-5"
                contentOverflow="hidden"
              >
                <OrganizationSalesListSection
                  organizationId={organization.id}
                  organizationName={organization.name}
                  surface="tile"
                />
              </OrganizationDetailPanel>
            ),
          },
          {
            id: 'studies-table',
            title: 'Studies table',
            description: 'Filter, sort, and open studies for this organization.',
            layout: {
              x: 0,
              y: 9,
              w: 4,
              h: 4,
              minW: 2,
              minH: 2,
              maxW: 6,
              maxH: 5,
              isResizable: true,
            },
            render: (layout) => (
              <OrganizationDetailPanel
                title="Studies"
                span="4x4"
                panelId="studies-table"
                currentSpan={panelSpanFromLayout(layout, '4x4')}
                contentClassName="-mx-5 -mb-5"
                contentOverflow="hidden"
              >
                <ProjectsListSection
                  fixedOrganizationId={organization.id}
                  showOrganizationFilter={false}
                  showOrganizationColumn={false}
                  preferenceScope={`list:organization:${organization.id}:studies`}
                  surface="tile"
                />
              </OrganizationDetailPanel>
            ),
          },
          {
            id: 'interviews-table',
            title: 'Interviews table',
            description: 'Filter, sort, and open interviews for this organization.',
            layout: {
              x: 0,
              y: 13,
              w: 4,
              h: 4,
              minW: 2,
              minH: 2,
              maxW: 6,
              maxH: 5,
              isResizable: true,
            },
            render: (layout) => (
              <OrganizationDetailPanel
                title="Interviews"
                span="4x4"
                panelId="interviews-table"
                currentSpan={panelSpanFromLayout(layout, '4x4')}
                contentClassName="-mx-5 -mb-5"
                contentOverflow="hidden"
              >
                <ProjectInterviewsListSection
                  contextLabel={organization.name}
                  preferenceScope={`list:organization:${organization.id}:interviews`}
                  loadPage={loadOrganizationInterviews}
                  surface="tile"
                />
              </OrganizationDetailPanel>
            ),
          },
          {
            id: 'members-table',
            title: 'Members table',
            description: 'Filter, sort, and manage organization members and invitations.',
            layout: {
              x: 0,
              y: 17,
              w: 4,
              h: 4,
              minW: 2,
              minH: 2,
              maxW: 6,
              maxH: 5,
              isResizable: true,
            },
            render: (layout) => (
              <OrganizationDetailPanel
                title="Members"
                span="4x4"
                panelId="members-table"
                currentSpan={panelSpanFromLayout(layout, '4x4')}
                contentClassName="-mx-5 -mb-5"
                contentOverflow="hidden"
              >
                <OrganizationMembersSection
                  organizationId={organization.id}
                  organizationName={organization.name}
                  surface="tile"
                />
              </OrganizationDetailPanel>
            ),
          },
          {
            id: 'tags',
            title: 'Tags',
            description: 'Tags assigned to this organization.',
            layout: {
              x: 0,
              y: 21,
              w: 2,
              h: 1,
              minW: 1,
              minH: 1,
              maxW: 4,
              maxH: 3,
              isResizable: true,
            },
            render: (layout) => (
              <TagsTile
                entityType="organization"
                entityId={organization.id}
                panelId="tags"
                currentSpan={panelSpanFromLayout(layout, '2x1')}
              />
            ),
          },
        ]}
      />
    </section>
  );
}
